import { cn } from "@/lib/utils";
import imgGrizk from "@/assets/lectures/grizk-i-save.png";
import imgNustok from "@/assets/lectures/nustok-buti-visu-atrama.png";
import imgPasakyti from "@/assets/lectures/pasakyti-ir-nesusipykti.png";
import imgSustiprink from "@/assets/lectures/sustiprink-emocini-atsparuma.png";
import imgTapk from "@/assets/lectures/tapk-charizmatiska.png";
import imgNedekinga from "@/assets/lectures/tu-nedekinga-kale.png";

/**
 * Editorial Bauhaus lesson artwork.
 * Known lecture titles render as bespoke raster artwork; unknown seeds fall back
 * to the deterministic SVG composition system below (used by hero rotators etc).
 */

export type LectureArtTheme = { src: string; bg: string; tone: "light" | "dark" };

/** Map normalized title -> imported asset + the full-bleed fill behind PNG alpha corners */
const TITLE_ART: Record<string, LectureArtTheme> = {
  "grizk i save": { src: imgGrizk, bg: "hsl(var(--ink))", tone: "dark" },
  "nustok buti visu atrama": { src: imgNustok, bg: "hsl(var(--yellow))", tone: "light" },
  "pasakyti ir nesusipykti": { src: imgPasakyti, bg: "hsl(var(--cream))", tone: "light" },
  "sustiprink emocini atsparuma": { src: imgSustiprink, bg: "hsl(var(--ink))", tone: "dark" },
  "tapk charizmatiska": { src: imgTapk, bg: "hsl(var(--red))", tone: "dark" },
  "tu nedekinga kale": { src: imgNedekinga, bg: "hsl(var(--yellow))", tone: "light" },
};

function normalizeTitle(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function getLectureArt(title?: string | null): string | null {
  if (!title) return null;
  return TITLE_ART[normalizeTitle(title)]?.src ?? null;
}

export function getLectureArtTheme(title?: string | null): LectureArtTheme | null {
  if (!title) return null;
  return TITLE_ART[normalizeTitle(title)] ?? null;
}

export type LessonArtVariant =
  | "eclipse"      // huge sun rising from bottom edge
  | "dot-field"   // dense gradient dot field, off-center
  | "halved"      // two color planes split by diagonal, sphere on the seam
  | "orbit-arc"   // bold ring sliced by edge + small satellite
  | "tower"       // stacked bars receding to a single dot
  | "twin-moons"; // two intersecting circles, color overlap

const VARIANTS: LessonArtVariant[] = [
  "eclipse",
  "dot-field",
  "halved",
  "orbit-arc",
  "tower",
  "twin-moons",
];

type Palette = {
  /** main background fill */
  bg: string;
  /** primary shape color */
  fg: string;
  /** accent color for secondary shape */
  accent: string;
};

const PALETTES: Palette[] = [
  // cream + red sun + ink
  { bg: "hsl(var(--cream))", fg: "hsl(var(--red))", accent: "hsl(var(--ink))" },
  // mustard + ink + red
  { bg: "hsl(var(--yellow))", fg: "hsl(var(--ink))", accent: "hsl(var(--red))" },
  // red + cream + ink
  { bg: "hsl(var(--red))", fg: "hsl(var(--cream))", accent: "hsl(var(--ink))" },
  // ink + mustard + red
  { bg: "hsl(var(--ink))", fg: "hsl(var(--yellow))", accent: "hsl(var(--red))" },
  // cream-soft + ink + mustard
  { bg: "hsl(var(--cream-soft))", fg: "hsl(var(--ink))", accent: "hsl(var(--yellow))" },
  // mustard + red + ink (warm pair)
  { bg: "hsl(var(--yellow))", fg: "hsl(var(--red))", accent: "hsl(var(--ink))" },
];

interface LessonArtworkProps {
  variant?: LessonArtVariant;
  className?: string;
  seed?: string | number;
  paletteIndex?: number;
  /** When provided and matched against the bespoke set, renders the raster artwork. */
  title?: string | null;
}

function hash(s: string | number) {
  const str = String(s);
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

export function pickArt(seed: string | number, indexOffset = 0) {
  const h = hash(seed) + indexOffset;
  return {
    variant: VARIANTS[h % VARIANTS.length],
    paletteIndex: (h >> 5) % PALETTES.length,
  };
}

const LessonArtwork = ({ variant, className, seed, paletteIndex, title }: LessonArtworkProps) => {
  const bespoke = getLectureArtTheme(title);
  if (bespoke) {
    return (
      <div
        className={cn("relative w-full h-full overflow-hidden", className)}
        style={{ backgroundColor: bespoke.bg }}
        aria-hidden
      >
        <img
          src={bespoke.src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover scale-[1.06]"
          loading="lazy"
        />
      </div>
    );
  }

  const auto = seed !== undefined ? pickArt(seed) : null;
  const v = variant ?? auto?.variant ?? "eclipse";
  const idx = paletteIndex ?? auto?.paletteIndex ?? 0;
  const p = PALETTES[idx % PALETTES.length] ?? PALETTES[0];

  return (
    <div className={cn("relative w-full h-full overflow-hidden", className)} aria-hidden>
      <svg
        viewBox="0 0 100 100"
        className="absolute inset-0 w-full h-full block"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Full-bleed background */}
        <rect x="0" y="0" width="100" height="100" fill={p.bg} />

        {v === "eclipse" && (
          <>
            {/* Half-sun rising from bottom — Saul Bass style */}
            <circle cx="50" cy="100" r="48" fill={p.fg} />
            {/* tiny satellite */}
            <circle cx="78" cy="22" r="2.4" fill={p.accent} />
          </>
        )}

        {v === "dot-field" && (
          <>
            {Array.from({ length: 11 }).map((_, r) =>
              Array.from({ length: 11 }).map((_, c) => {
                const x = 6 + c * 8.8;
                const y = 6 + r * 8.8;
                // radial falloff from a point
                const dx = x - 30;
                const dy = y - 30;
                const d = Math.sqrt(dx * dx + dy * dy);
                const radius = Math.max(0.4, 3.4 - d / 22);
                const useAccent = (r + c) % 7 === 0;
                return (
                  <circle
                    key={`${r}-${c}`}
                    cx={x}
                    cy={y}
                    r={radius}
                    fill={useAccent ? p.accent : p.fg}
                  />
                );
              })
            )}
          </>
        )}

        {v === "halved" && (
          <>
            {/* Diagonal color plane */}
            <polygon points="0,0 100,0 0,100" fill={p.fg} />
            {/* Sphere balanced on the seam */}
            <circle cx="62" cy="58" r="22" fill={p.accent} />
            <circle cx="62" cy="58" r="22" fill={p.bg} opacity="0.0" />
          </>
        )}

        {v === "orbit-arc" && (
          <>
            {/* Bold off-canvas ring */}
            <circle
              cx="20"
              cy="80"
              r="58"
              fill="none"
              stroke={p.fg}
              strokeWidth="6"
            />
            {/* Solid mass */}
            <circle cx="68" cy="34" r="14" fill={p.fg} />
            {/* Spark */}
            <circle cx="86" cy="62" r="3" fill={p.accent} />
          </>
        )}

        {v === "tower" && (
          <>
            {/* Stacked horizontal bars receding upward */}
            {Array.from({ length: 8 }).map((_, i) => {
              const y = 88 - i * 8;
              const w = 70 - i * 7;
              const x = 50 - w / 2;
              return (
                <rect
                  key={i}
                  x={x}
                  y={y}
                  width={w}
                  height={4}
                  fill={p.fg}
                  opacity={0.5 + i * 0.06}
                />
              );
            })}
            {/* Capstone dot */}
            <circle cx="50" cy="22" r="3.5" fill={p.accent} />
          </>
        )}

        {v === "twin-moons" && (
          <>
            {/* Two large circles with overlap */}
            <circle cx="38" cy="50" r="26" fill={p.fg} />
            <circle cx="64" cy="50" r="26" fill={p.accent} opacity="0.92" />
            {/* tiny eclipse mark */}
            <circle cx="51" cy="50" r="3" fill={p.bg} />
          </>
        )}
      </svg>
    </div>
  );
};

export default LessonArtwork;
